import type { TableCellProperties } from "@kaleidoscope-solutions/engine-wasm"
import {
  CellDisplayKind,
  CellKind,
  ExpandCollapse,
  Gap,
  Panel,
  SortDirection,
} from "@kaleidoscope-solutions/engine-wasm"

export interface Panels {
  Corner: Cell[][]
  Columns: Cell[][]
  Rows: Cell[][]
  Body: Cell[][]
}

interface GridColumnInfo {
  icon: boolean
  chips: boolean
  readonly: boolean
  rowGroup: boolean
  detailPage: boolean
  charCount: number
  emptyRelationship: boolean
  displayKind: CellDisplayKind[]
}

// Parsed from a WASM JSON grid string into a pure Javascript dense model representing the grid.
// Iterating the grid will yield zero or more Rows, iterating a Row will yield zero or more Cells.
export class Grid implements Iterable<Row> {
  private columnCount!: number

  private id!: string[]

  private panel!: string[]

  // Cell spanning
  private colspan!: number[]
  private rowspan!: number[]
  private hiddenBySpan!: boolean[]

  // Display
  private icon!: string[]
  private displayKind!: string[]
  private backgroundColour!: string[]
  private readOnly!: boolean[]
  private formattedValue!: string[]
  private valueType!: string[]
  private cellKind!: string[]
  private adjacentCellGaps!: string[][]
  private emptyRelationship!: boolean[]
  private expandCollapse!: string[]
  private detailPageType?: string[]

  // Sort
  private sortable!: boolean[]
  private sortDirection!: string[]

  // Position
  private firstCellInRow!: boolean[]
  private lastCellInRow!: boolean[]
  private firstRow!: boolean[]
  private lastRow!: boolean[]
  private hierarchyLevel!: number[]
  private nextRowNested!: boolean[]

  // Cache for colWidths
  private _calculatedColumnWidth?: number[]

  get rowCount(): number {
    if (!this.id || this.id.length === 0) {
      return 0
    }

    const rowCount = Math.ceil(this.id.length / this.columnCount)

    return rowCount
  }

  get colCount(): number {
    return this.columnCount
  }

  get colWidths() {
    // Compute these only once
    if (this._calculatedColumnWidth) {
      return this._calculatedColumnWidth
    }

    this._calculatedColumnWidth = estimateColumnWidths(this)

    return this._calculatedColumnWidth
  }

  cellColWidth(column: number) {
    return this.colWidths[column]
  }

  index(row: number, column: number) {
    return row * this.columnCount + column
  }

  cellId(row: number, column: number): string {
    return this.id[this.index(row, column)]
  }

  cellIcon(row: number, column: number): string {
    return this.icon[this.index(row, column)]
  }

  cellPanel(row: number, column: number): string {
    return this.panel[this.index(row, column)]
  }

  cellColspan(row: number, column: number): number {
    return this.colspan[this.index(row, column)]
  }

  cellRowspan(row: number, column: number): number {
    return this.rowspan[this.index(row, column)]
  }

  cellDisplayKind(row: number, column: number): CellDisplayKind {
    const cellDisplayKind = this.displayKind[this.index(row, column)]
    return CellDisplayKind[cellDisplayKind as any] as unknown as CellDisplayKind
  }

  cellBackgroundColour(row: number, column: number): string[] | undefined {
    const colour = this.backgroundColour[this.index(row, column)]
    if (typeof colour === "string") {
      return colour.split(", ")
    }
    return undefined
  }

  cellHiddenBySpan(row: number, column: number): boolean {
    return this.hiddenBySpan[this.index(row, column)]
  }

  cellReadOnly(row: number, column: number): boolean {
    return this.readOnly[this.index(row, column)]
  }

  cellFormattedValue(row: number, column: number): string {
    return this.formattedValue[this.index(row, column)]
  }

  cellValueType(row: number, column: number): string | undefined {
    const valueType = this.valueType[this.index(row, column)]
    return valueType == null ? undefined : valueType
  }

  cellCellKind(row: number, column: number): CellKind | undefined {
    const cellKind = this.cellKind[this.index(row, column)]
    return cellKind == null
      ? undefined
      : (CellKind[cellKind as any] as unknown as CellKind)
  }

  cellAdjacentCellGaps(row: number, column: number): Gap[] {
    const gaps =
      this.adjacentCellGaps[this.index(row, column)] ?? ([] as unknown[])
    return gaps.map((key: any) => Gap[key as any] as unknown) as Gap[]
  }

  cellFirstCellInRow(row: number, column: number): boolean {
    return this.firstCellInRow[this.index(row, column)]
  }

  cellLastCellInRow(row: number, column: number): boolean {
    return this.lastCellInRow[this.index(row, column)]
  }

  cellFirstRow(row: number, column: number): boolean {
    return this.firstRow[this.index(row, column)]
  }

  cellLastRow(row: number, column: number): boolean {
    return this.lastRow[this.index(row, column)]
  }

  cellHierarchyLevel(row: number, column: number): number {
    return this.hierarchyLevel[this.index(row, column)]
  }

  cellNextRowNested(row: number, column: number): boolean {
    return this.nextRowNested[this.index(row, column)]
  }

  cellEmptyRelationship(row: number, column: number): boolean {
    return this.emptyRelationship[this.index(row, column)]
  }

  cellSortable(row: number, column: number): boolean {
    return this.sortable[this.index(row, column)]
  }

  cellSortDirection(row: number, column: number): SortDirection {
    const sortDirection = this.sortDirection[this.index(row, column)]
    return SortDirection[sortDirection as any] as unknown as SortDirection
  }

  cellExpandCollapse(row: number, column: number): ExpandCollapse {
    const expandCollapse = this.expandCollapse[this.index(row, column)]
    return ExpandCollapse[expandCollapse as any] as unknown as ExpandCollapse
  }

  cellDetailPageType(row: number, column: number): string | undefined {
    return this.detailPageType?.[this.index(row, column)]
  }

  // Find last row index with a display kind of CellDisplayKind.Cell
  // We don't want to find Summary or Gaps.
  findLastCellRowIndex(): number {
    for (let row = this.rowCount; row--; row >= 0) {
      const cell = new Cell(row, 0, this)
      if (cell.displayKind === CellDisplayKind.Cell) {
        return row
      }
    }
    return -1
  }

  [Symbol.iterator]() {
    let rowCounter = 0
    const row = new Row(rowCounter, this)
    return {
      next: () => {
        if (rowCounter < this.rowCount) {
          row.row = rowCounter
        }
        rowCounter++

        return {
          done: rowCounter > this.rowCount,
          value: row,
        }
      },
    }
  }
}

// This should not be instantiated directly.
export class Row implements Iterable<Cell> {
  private _row: number
  private _grid: Grid

  constructor(row: number, grid: Grid) {
    validateRow(row, grid)
    this._row = row
    this._grid = grid
  }

  get row(): number {
    return this._row
  }

  set row(row: number) {
    validateRow(row, this._grid)
    this._row = row
  }

  [Symbol.iterator]() {
    let columnCounter = 0

    return {
      next: () => {
        const cell =
          columnCounter < this._grid.colCount
            ? new Cell(this._row, columnCounter, this._grid)
            : new Cell(0, 0, this._grid)
        columnCounter++

        return {
          done: columnCounter > this._grid.colCount,
          value: cell,
        }
      },
    }
  }
}

// Properties pulled from TableCellProperties
type TableCellPropertiesKeys = {
  [key in keyof TableCellProperties]: unknown
}

export interface GridCell extends TableCellPropertiesKeys {
  id: string // panel, row, col eg. Body-4-15
  panel: string
  readOnly: boolean
  displayKind: CellDisplayKind
  backgroundColour: string[] | undefined
  valueType: string | undefined
  formattedValue: string // value
  emptyRelationship: boolean
  expandCollapse: ExpandCollapse

  // Sort
  sortable: boolean
  sortDirection: SortDirection

  // Spans
  colspan: number
  rowspan: number
  hiddenBySpan: boolean

  cellKind: CellKind | undefined
  adjacentCellGaps: Gap[]
  hierarchyLevel: number
  nextRowNested: boolean

  // Position
  firstCellInRow: boolean
  lastCellInRow: boolean
  firstRow: boolean
  lastRow: boolean
}

export class Cell implements GridCell {
  private _row: number
  private _column: number
  private _grid: Grid

  constructor(row: number, column: number, grid: Grid) {
    validateCoordinates(row, column, grid)
    this._row = row
    this._column = column
    this._grid = grid
  }

  setPosition(row: number, column: number) {
    validateCoordinates(row, column, this._grid)
    this._row = row
    this._column = column
  }

  get columnCount(): number {
    return this._grid.colCount
  }

  get columnWidth(): number | undefined {
    return this._grid.cellColWidth(this._column)
  }

  get columnWidths(): (number | undefined)[] {
    return this._grid.colWidths
  }

  get row(): number {
    return this._row
  }

  get column(): number {
    return this._column
  }

  get id(): string {
    return this._grid.cellId(this._row, this._column)
  }

  get icon(): string {
    return this._grid.cellIcon(this._row, this._column)
  }

  get panel(): string {
    return this._grid.cellPanel(this._row, this._column)
  }

  get colspan(): number {
    return this._grid.cellColspan(this._row, this._column)
  }

  get rowspan(): number {
    return this._grid.cellRowspan(this._row, this._column)
  }

  get displayKind(): CellDisplayKind {
    return this._grid.cellDisplayKind(this._row, this._column)
  }

  get backgroundColour(): string[] | undefined {
    return this._grid.cellBackgroundColour(this._row, this._column)
  }

  get hiddenBySpan(): boolean {
    return this._grid.cellHiddenBySpan(this._row, this._column)
  }

  get readOnly(): boolean {
    return this._grid.cellReadOnly(this._row, this._column)
  }

  get formattedValue(): string {
    return this._grid.cellFormattedValue(this._row, this._column)
  }

  get valueType(): string | undefined {
    return this._grid.cellValueType(this._row, this._column)
  }

  get cellKind(): CellKind | undefined {
    return this._grid.cellCellKind(this._row, this._column)
  }

  get adjacentCellGaps(): Gap[] {
    return this._grid.cellAdjacentCellGaps(this._row, this._column)
  }

  get firstCellInRow(): boolean {
    return this._grid.cellFirstCellInRow(this._row, this._column)
  }

  get lastCellInRow(): boolean {
    return this._grid.cellLastCellInRow(this._row, this._column)
  }

  get firstRow(): boolean {
    return this._grid.cellFirstRow(this._row, this._column)
  }

  get lastRow(): boolean {
    return this._grid.cellLastRow(this._row, this._column)
  }

  get hierarchyLevel(): number {
    return this._grid.cellHierarchyLevel(this._row, this._column)
  }

  get nextRowNested(): boolean {
    return this._grid.cellNextRowNested(this._row, this._column)
  }

  get emptyRelationship(): boolean {
    return this._grid.cellEmptyRelationship(this._row, this._column)
  }

  get sortable(): boolean {
    return this._grid.cellSortable(this._row, this._column)
  }

  get sortDirection(): SortDirection {
    return this._grid.cellSortDirection(this._row, this._column)
  }

  get expandCollapse(): ExpandCollapse {
    return this._grid.cellExpandCollapse(this._row, this._column)
  }

  get detailPageType(): string | undefined {
    return this._grid.cellDetailPageType(this._row, this._column)
  }
}

export function gridFromCellProperties(
  cellProperties: TableCellProperties
): Grid {
  return Object.assign(new Grid(), cellProperties)
}

export function gridToPanels(grid: Grid): Panels {
  const panels: Panels = {
    Corner: [], Columns: [], Rows: [], Body: []
  }

  for (const row of grid) {
    const tempCornerRow: Cell[] = []
    const tempColumnsRow: Cell[] = []
    const tempRowsRow: Cell[] = []
    const tempBodyRow: Cell[] = []
    for (const cell of row) {
      if (cell.panel === Panel[Panel.Corner]) {
        tempCornerRow.push(cell)
      }
      if (cell.panel === Panel[Panel.Columns]) {
        tempColumnsRow.push(cell)
      }
      if (cell.panel === Panel[Panel.Rows]) {
        tempRowsRow.push(cell)
      }
      if (cell.panel === Panel[Panel.Body]) {
        tempBodyRow.push(cell)
      }
    }
    if (tempCornerRow.length > 0) {
      panels.Corner.push(tempCornerRow)
    }
    if (tempColumnsRow.length > 0) {
      panels.Columns.push(tempColumnsRow)
    }
    if (tempRowsRow.length > 0) {
      panels.Rows.push(tempRowsRow)
    }
    if (tempBodyRow.length > 0) {
      panels.Body.push(tempBodyRow)
    }
  }

  return panels
}

export function panelsFromCellProperties(cellProperties: TableCellProperties): Panels {
  const grid = gridFromCellProperties(cellProperties)
  return gridToPanels(grid)
}

function validateCoordinates(row: number, column: number, grid: Grid) {
  validateRow(row, grid)
  validateColumn(column, grid)
}

function validateRow(row: number, grid: Grid) {
  if (row >= grid.rowCount) {
    throw new Error(
      `row ${row} is outside of the grid bounds (${grid.rowCount})`
    )
  }
}

function validateColumn(column: number, grid: Grid) {
  if (column >= grid.colCount) {
    throw new Error(
      `column ${column} is outside of the grid bounds (${grid.colCount})`
    )
  }
}

function estimateColumnWidths(grid: Grid) {
  const columns: number[] = []

  for (let col = 0; col < grid.colCount; col++) {
    columns[col] = estimateColumnSize(grid, col)
  }

  return columns
}

export function estimateColumnSize(grid: Grid, column: number): number {
  const info = columnInfo(grid, column)
  return estimateColumnWidth(info)
}

function columnInfo(grid: Grid, column: number): GridColumnInfo {
  const info: GridColumnInfo = {
    displayKind: [],
    icon: false,
    chips: false,
    readonly: false,
    rowGroup: false,
    detailPage: false,
    charCount: 0,
    emptyRelationship: false
  }

  info.charCount = longestLengthInColumn(grid, column)

  for (let row = 0; row < grid.rowCount; row++) {
    const displayKind = grid.cellDisplayKind(row, column)

    if (!info.displayKind.includes(displayKind)) {
      info.displayKind.push(displayKind)
    }

    if (info.chips === false) {
      const cellValueType = grid.cellValueType(row, column)
      if (cellValueType === "Entity" || cellValueType === "List") {
        info.chips = true
      }
    }
    if (info.icon === false) {
      const cellIcon = grid.cellIcon(row, column)
      if (cellIcon) {
        info.icon = true
      }
    }
    if (info.readonly === false) {
      const cellReadonly = grid.cellReadOnly(row, column)
      if (cellReadonly) {
        info.readonly = true
      }
    }
    if (info.rowGroup === false) {
      const hierarchyLevel = grid.cellHierarchyLevel(row, column)
      if (hierarchyLevel > 0) {
        info.rowGroup = true
      }
    }
    if (info.detailPage === false) {
      const detailPageType = grid.cellDetailPageType(row, column)
      if (detailPageType) {
        info.detailPage = true
      }
    }
    if (info.emptyRelationship === false) {
      const emptyRelationship = grid.cellEmptyRelationship(row, column)
      if (emptyRelationship) {
        info.emptyRelationship = true
      }
    }
  }

  return info
}

function estimateColumnWidth(info: GridColumnInfo): number {
  if (info.displayKind.every((kind) => kind === CellDisplayKind.Gap)) {
    return 8
  }

  let padding = 40

  // Add padding for entity and lists that display as chips
  if (info.chips) {
    padding += 12
  }

  // Add padding for bundles icon (or any future icons)
  if (info.icon) {
    padding += 20
  }

  // Add padding for read-only cells
  if (info.readonly) {
    padding += 20
  }

  // Add padding for the magic fill icon in cell headers
  if (info.emptyRelationship) {
    padding += 28
  }

  // Add padding for details page icon in cell headers
  if (info.detailPage) {
    padding += 24
  }

  // Extra padding for collapse/expand in row headers
  if (info.rowGroup) {
    padding += 20
  }

  // 9 for char width
  return info.charCount * 9 + padding
}

/**
 * Returns the longest string length in a single column of the grid.
 * @param grid The Grid instance
 * @param column The column index
 */
export function longestLengthInColumn(grid: Grid, column: number): number {
  let longest = 0
  for (let row = 0; row < grid.rowCount; row++) {
    // Find the source cell that actually owns this column (account for colspan/hidden spans)
    let sourceCol = column
    // Walk left until we find the originating cell for a span (hiddenBySpan is true on spanned cells)
    while (sourceCol > 0 && grid.cellHiddenBySpan(row, sourceCol)) {
      sourceCol--
    }

    const fullLength = grid.cellFormattedValue(row, sourceCol).length
    const colspan = Math.max(1, grid.cellColspan(row, sourceCol))
    const perCol = Math.ceil(fullLength / colspan)

    if (perCol > longest) {
      longest = perCol
    }
  }
  return longest
}
